
import React from "react";
import { useState, useEffect, useMemo, useRef } from "react";

import { TrendingUp, TrendingDown, Minus, ArrowUpDown, ArrowUp, ArrowDown, RotateCcw, AlertTriangle } from "lucide-react";
import { useClasseContext } from "../../../context/ClasseContext";
import { TEXTES_COMPREHENSION, nomDuTexte, rangDuTexte } from "../EvalNat/evaluationConfig";
// Le début de l'énoncé d'une question du QCM de suivi (12/10/2026) : deux variantes d'un même item portaient le
// même numéro et le même libellé (« Q01 » deux fois), rien ne les distinguait dans la liste
const extraitDeLEnonce = (texte) => {
  const brut = String(texte || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  return brut.length > 90 ? `${brut.slice(0, 88).trimEnd()}…` : brut;
};

function TestSpecifiqueBilan({ testSpecifique, EvalNat, evalBilan, handleItemClick, questionsBilan = [] }) {
  const extraits = useMemo(
    () => new Map((questionsBilan || []).map((q) => [String(q.id), extraitDeLEnonce(q.texte)])),
    [questionsBilan]
  );

  const {classeNom} = useClasseContext();
  const labelAutomatismes = useMemo(() => {
  
    return EvalNat.questions
      .filter(question => question.type_label === "Automatisme")
      .reduce((acc, question) => {
        acc[question.question_num] = question.nom_label;
        return acc;
      }, {});
  }, [EvalNat]); // Les dépendances sont les questions de EvalNat

  const labelProblèmes = useMemo(() => {

    return EvalNat.questions
      .filter(question => question.type_label === "Résolution de problèmes")
      .reduce((acc, question) => {
        acc[question.question_num] = question.nom_label;
        return acc;
      }, {});
  }, [EvalNat]); // Les dépendances sont les questions de EvalNat

   // Pour les labels simples (comme Automatisme et Problèmes)
const labelLexique = useMemo(() => {
  if (!EvalNat) return {};
  return EvalNat.questions
    .filter(question => question.type_label === "Lexique")
    .reduce((acc, question) => {
      acc[question.question_num] = question.nom_label;
      return acc;
    }, {});
}, [EvalNat]);


const labelComprehensionEcrite = useMemo(() => {
  if (!EvalNat) return {};
  return EvalNat.questions
    .filter(question => question.type_label.startsWith("Compréhension de l'écrit"))
    .reduce((acc, question) => {
      acc[`${nomDuTexte(question.type_label)} ${question.question_num}`] = question.nom_label;
      return acc;
    }, {});
}, [EvalNat]);



const labelCompetencesLangagieres = useMemo(() => {
  if (!EvalNat) return {};
  return EvalNat.questions
    .filter(question => question.type_label === "Compétences langagières et linguistiques")
    .reduce((acc, question) => {
      acc[question.question_num] = question.nom_label;
      return acc;
    }, {});
}, [EvalNat]);


    const [statistiques, setStatistiques] = useState({});
    const [activeItemId, setActiveItemId] = useState(null);
   const [activeQuestion, setActiveQuestion] = useState('Q1');
   const [sortByProgression, setSortByProgression] = useState(null); // null, 'asc', 'desc'



    const calculerStatistiques = (elevesReponses, testSpecifique) => {

    let stats = {};

    // Les textes de compréhension de l'écrit, d'hier et d'aujourd'hui (evaluationConfig)
    const comprehensionEcriteCategories = TEXTES_COMPREHENSION.map((t) => t.nom);

    elevesReponses.forEach(eleve => {
        if (eleve.reponses) {
            // Si c'est ComprehensionEcrite, traiter toutes les sous-catégories
            if (testSpecifique === "ComprehensionEcrite") {
                comprehensionEcriteCategories.forEach(category => {
                    if (eleve.reponses[category]) {
                        Object.entries(eleve.reponses[category]).forEach(([question, data]) => {
                            // Créer une clé unique pour chaque question
                            const questionKey = `${category} ${question}`;
                            
                            if (!stats[questionKey]) {
                                stats[questionKey] = { 
                                    reussites: [], 
                                    echecs: [], 
                                    question_num: question,
                                    category: category 
                                };
                            }
                            
                            const eleveData = { 
                                classe: eleve.classe_nom, 
                                nom: eleve.nom_eleve, 
                                réponse: data?.réponse || '' 
                            };
                            
                            if (data.score === 1) {
                                stats[questionKey].reussites.push(eleveData);
                            } else {
                                stats[questionKey].echecs.push(eleveData);
                            }
                        });
                    }
                });
            } 
             // Cas spécifique pour CompetencesLangagieres
        else if (testSpecifique === "CompetencesLangagieres") {
            // Les questions sont dans eleve.reponses.Compétence
            if (eleve.reponses.Compétence) {
                Object.entries(eleve.reponses.Compétence).forEach(([question, data]) => {
                    if (!stats[question]) {
                        stats[question] = { 
                            reussites: [], 
                            echecs: [], 
                            question_num: question 
                        };
                    }
                    
                    const eleveData = { 
                        classe: eleve.classe_nom, 
                        nom: eleve.nom_eleve, 
                        réponse: data?.réponse || '' 
                    };
                    
                    if (data.score === 1) {
                        stats[question].reussites.push(eleveData);
                    } else {
                        stats[question].echecs.push(eleveData);
                    }
                });
            }
        } else {
                // Traitement normal pour les autres catégories
                if (eleve.reponses[testSpecifique]) {
                    Object.entries(eleve.reponses[testSpecifique]).forEach(([question, { score, réponse }]) => {
                        if (!stats[question]) {
                            stats[question] = { reussites: [], echecs: [], question_num: question };
                        }
                        
                        const eleveData = { 
                            classe: eleve.classe_nom, 
                            nom: eleve.nom_eleve, 
                            réponse: réponse || '' 
                        };
                        
                        if (score === 1) {
                            stats[question].reussites.push(eleveData);
                        } else {
                            stats[question].echecs.push(eleveData);
                        }
                    });
                }
            }
        }
    });
    
    return stats;
    }

    // A OPTIMISER !!!

    const calculerStatistiquesevalBilan = (elevesReponses, testSpecifique) => {


      let stats = {};

       // Les textes de compréhension de l'écrit, d'hier et d'aujourd'hui, sous leur type_label (evaluationConfig)
    const comprehensionEcriteCategories = TEXTES_COMPREHENSION.map((t) => t.typeLabel);

     elevesReponses.forEach(eleve => {
        if (eleve.reponses) {
            // Si c'est ComprehensionEcrite, traiter toutes les sous-catégories
            if (testSpecifique === "ComprehensionEcrite") {
                comprehensionEcriteCategories.forEach(category => {
                    if (eleve.reponses[category]) {
                        Object.entries(eleve.reponses[category]).forEach(([question, data]) => {
                            // Le nom court du texte (Arachné, Sommeil, Petit Chose…)
                            const shortCategory = nomDuTexte(category);

                            
                            if (!stats[question]) {
                                stats[question] = {
                                    reussites: [],
                                    echecs: [],
                                    question_num: data.question_num || question,
                                    displayLabel: `${shortCategory} ${data.question_num || question}`,
                                    id: question,
                                    category: shortCategory
                                };
                            }

                            const eleveData = {
                                classe: eleve.classe_nom,
                                nom: eleve.nom_eleve,
                                réponse: data?.réponse || ''
                            };

                            if (data.score === 1) {
                                stats[question].reussites.push(eleveData);
                            } else {
                                stats[question].echecs.push(eleveData);
                            }
                        });
                    }
                });
            }
            // Cas spécifique pour CompetencesLangagieres
            else if (testSpecifique === "CompetencesLangagieres") {
           
                if (eleve.reponses['Compétences langagières et linguistiques']) {
                    Object.entries(eleve.reponses['Compétences langagières et linguistiques']).forEach(([question, data]) => {
                        if (!stats[question]) {
                            stats[question] = {
                                reussites: [],
                                echecs: [],
                                question_num: data.question_num || question,
                                displayLabel: data.question_num || question,
                                id: question
                            };
                        }

                        const eleveData = {
                            classe: eleve.classe_nom,
                            nom: eleve.nom_eleve,
                            réponse: data?.réponse || ''
                        };

                        if (data.score === 1) {
                            stats[question].reussites.push(eleveData);
                        } else {
                            stats[question].echecs.push(eleveData);
                        }
                    });
                }
            }
            // Cas pour Automatismes et Problèmes
            else {
                let tache = "";
                if(testSpecifique === "Automatismes"){
                    tache = "Automatisme";
                } else if(testSpecifique === 'Problèmes'){
                    tache = "Résolution de problèmes";
                } else {
                    // Pour les autres cas, utiliser directement testSpecifique
                    tache = testSpecifique;
                }
                
                if (eleve.reponses[tache]) {
                    Object.entries(eleve.reponses[tache]).forEach(([question, data]) => {
                        let question_num = data.question_num || question;
                        if (!stats[question]) {
                            stats[question] = {
                                reussites: [],
                                echecs: [],
                                question_num: question_num,
                                displayLabel: question_num,
                                id: question
                            };
                        } else if (data.question_num && stats[question].displayLabel === question) {
                            // Corriger le displayLabel si un élève précédent n'avait pas question_num
                            stats[question].displayLabel = data.question_num;
                            stats[question].question_num = data.question_num;
                        }

                        const eleveData = {
                            classe: eleve.classe_nom,
                            nom: eleve.nom_eleve,
                            réponse: data?.réponse || ''
                        };

                        if (data.score === 1) {
                            stats[question].reussites.push(eleveData);
                        } else {
                            stats[question].echecs.push(eleveData);
                        }
                    });
                }
            }
        }
    });
    
    return stats;
}

    useEffect(() => {    
        const statsEvalNat = calculerStatistiques(EvalNat.eleves_reponses, testSpecifique);
        const statsEvalBilan = evalBilan ? calculerStatistiquesevalBilan(evalBilan, testSpecifique) : {};
        const statsCombinés = {
          evalNat : statsEvalNat,
          evalBilan : statsEvalBilan
        }
        setStatistiques(statsCombinés);
      }, [EvalNat, evalBilan, testSpecifique]);


    const handleClick = (statsbilan, statsnat , id , question_num, displayLabel) => {
       let objEvalBilan = {}
       if(evalBilan){
        objEvalBilan = {
            statsbilan : statsbilan,
            statsnat: statsnat,
            question_id : id,
            bilan : true,
            question_num : question_num,
            displayLabel : displayLabel,
          }
          handleItemClick(objEvalBilan);
       }
      else {
        handleItemClick(statsnat)
      }
        setActiveItemId(id);
    }

    // Récupérer les questions de la source de données active
  const activeStats = evalBilan ? statistiques.evalBilan : statistiques.evalNat;


  
  const hasAnyEvalNatData = !!(statistiques.evalNat && Object.keys(statistiques.evalNat).length > 0);

  // Fonction pour calculer le diffPercentage d'une entrée
  const computeDiff = ([question, statsBilan]) => {
    let pourcentageNat = 0;
    let pourcentageBil = 0;
    if (evalBilan && statistiques.evalBilan[question]) {
      const totalBilan = statsBilan.reussites.length + statsBilan.echecs.length;
      pourcentageBil = totalBilan > 0 ? Math.round((statsBilan.reussites.length / totalBilan) * 100) : 0;
      const statsNat = statistiques.evalNat[statsBilan.displayLabel || question];
      if (statsNat && statsNat.reussites && statsNat.echecs) {
        const totalNat = statsNat.reussites.length + statsNat.echecs.length;
        pourcentageNat = totalNat > 0 ? Math.round((statsNat.reussites.length / totalNat) * 100) : 0;
      } else {
        // Pas de données nationales pour cette question → diff neutre
        return 0;
      }
    } else {
      const totalNat = statsBilan.reussites.length + statsBilan.echecs.length;
      pourcentageNat = totalNat > 0 ? Math.round((statsBilan.reussites.length / totalNat) * 100) : 0;
    }
    return pourcentageBil - pourcentageNat;
  };

  const toggleSort = () => {
    setSortByProgression(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : prev === 'desc' ? 'asc' : null);
  };

  // Tri naturel par question_num (Q1, Q2, ... Q10)
  // Texte par texte (compréhension : Arachné Q01 à Q10, puis Sommeil Q01…), puis par numéro.
  const sortByQuestionNum = (a, b) => {
    const parTexte = rangDuTexte(a[1].category) - rangDuTexte(b[1].category);
    if (parTexte !== 0) return parTexte;
    const numA = parseInt((a[1].question_num || a[0]).replace(/\D/g, '')) || 0;
    const numB = parseInt((b[1].question_num || b[0]).replace(/\D/g, '')) || 0;
    return numA - numB;
  };

  const sortedEntries = activeStats ? (() => {
    const entries = Object.entries(activeStats);
    if (!sortByProgression) return [...entries].sort(sortByQuestionNum);
    return [...entries].sort((a, b) => {
      const diffA = computeDiff(a);
      const diffB = computeDiff(b);
      return sortByProgression === 'asc' ? diffA - diffB : diffB - diffA;
    });
  })() : [];

  const latestRef = useRef({ sortedEntries, evalBilan, statistiques, handleClick });
  latestRef.current = { sortedEntries, evalBilan, statistiques, handleClick };

  // Auto-sélectionner la première question au changement de catégorie
  useEffect(() => {
    // Petit délai pour laisser le temps aux stats de se calculer
    const timeoutId = setTimeout(() => {
      const { sortedEntries, evalBilan, statistiques, handleClick } = latestRef.current;

      if (sortedEntries.length > 0 && evalBilan) {
        const [question] = sortedEntries[0];
        if (statistiques.evalBilan?.[question]) {
          handleClick(statistiques.evalBilan, statistiques.evalNat, question, statistiques.evalBilan[question].question_num, statistiques.evalBilan[question].displayLabel);
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [testSpecifique]);

  return (
  <div className="max-h-[700px] border-slate-700 overflow-y-auto">
    {/* Bandeau si aucune donnée eval nat */}
    {evalBilan && !hasAnyEvalNatData && (
      <div className="mx-4 mt-3 mb-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Aucune donnée d'évaluation nationale disponible pour ces élèves. La progression ne peut pas être calculée.
        </p>
      </div>
    )}
    {/* Bouton de tri par progression */}
    {activeStats && evalBilan && hasAnyEvalNatData && (
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm px-4 py-2 border-b border-gray-200 flex justify-end gap-2">
        <button
          onClick={toggleSort}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
            sortByProgression
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {sortByProgression === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : sortByProgression === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUpDown className="w-3.5 h-3.5" />}
          Progression {sortByProgression === 'asc' ? '↑' : sortByProgression === 'desc' ? '↓' : ''}
        </button>
        {sortByProgression && (
          <button
            onClick={() => setSortByProgression(null)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Q1, Q2...
          </button>
        )}
      </div>
    )}
    {sortedEntries.map(([question, statsBilan]) => {
      // statsBilan contient les stats du BILAN (depuis activeStats = statistiques.evalBilan)

      let pourcentageNational = 0;
      let pourcentageBilan = 0;
      let hasNatData = false;

      // Si evalBilan existe, on a les stats du bilan dans statsBilan
      if(evalBilan && statistiques.evalBilan[question]) {
        // Calculer le pourcentage BILAN
        const totalReponsesBilan = statsBilan.reussites.length + statsBilan.echecs.length;
        pourcentageBilan = totalReponsesBilan > 0 ? Math.round((statsBilan.reussites.length / totalReponsesBilan) * 100) : 0;

        // Récupérer et calculer le pourcentage NATIONAL
        const statsNational = statistiques.evalNat[statsBilan.displayLabel || question];
        hasNatData = !!(statsNational?.reussites && statsNational?.echecs);
        if(hasNatData){
          const totalReponsesNational = statsNational.reussites.length + statsNational.echecs.length;
          pourcentageNational = totalReponsesNational > 0 ? Math.round((statsNational.reussites.length / totalReponsesNational) * 100) : 0;
        }
      } else {
        // Si pas d'evalBilan, statsBilan contient les stats nationales
        hasNatData = true;
        const totalReponsesNat = statsBilan.reussites.length + statsBilan.echecs.length;
        pourcentageNational = totalReponsesNat > 0 ? Math.round((statsBilan.reussites.length / totalReponsesNat) * 100) : 0;
      }

      return (
        <React.Fragment key={question}>
          {evalBilan && <div>
            <ProgressBar
              onClick={() => handleClick(statistiques.evalBilan, statistiques.evalNat, question, statistiques.evalBilan[question].question_num, statsBilan.displayLabel)}
              percentage={pourcentageBilan}  // ← BILAN
              pourcentageReference={pourcentageNational}  // ← NATIONAL
              hasNatData={hasNatData}
              label={statsBilan.displayLabel || question}
              extrait={extraits.get(String(statsBilan.id || question))}
              testSpecifique={testSpecifique}
              isActive={activeItemId === question}
              labelAutomatisme={labelAutomatismes}
              labelProblèmes={labelProblèmes}
              labelLexique={labelLexique}
              labelCompetencesLangagieres={labelCompetencesLangagieres}
              labelComprehensionEcrite={labelComprehensionEcrite}
            />
          </div>}
          {/* {!evalBilan && <div
            key={question}
            onClick={() => handleEvalNatClick({ question, reussites: statsBilan.reussites, echecs: statsBilan.echecs }, question)}
            className={`px-5 py-4 border-b border-gray-100 cursor-pointer transition-all flex flex-col items-center gap-3 ${
              activeQuestion === question
                ? 'bg-sky-100 border-l-4 border-l-blue-600'
                : 'hover:bg-gray-50 hover:translate-x-1'
            }`}
          >
            <div className="flex flex-row gap-2">
              <span className="font-semibold text-base text-left text-blue-600 min-w-[35px]">
                {question}
              </span>
              <span className="flex-1 text-gray-800 font-semibold text-sm">
                {getLabel(question, testSpecifique)}
              </span>
            </div>
            <ProgressBar 
              onClick={() => handleEvalNatClick({ question, reussites: statsBilan.reussites, echecs: statsBilan.echecs }, question)}
              pourcentageReference={pourcentageNational}
            />
          </div>} */}
        </React.Fragment>
      );
    })}
  </div>
);
};


export default TestSpecifiqueBilan;




export const ProgressBar = ({ percentage, label, extrait, onClick, isActive, testSpecifique, pourcentageReference, hasNatData = true, labelAutomatisme, labelProblèmes, labelLexique, labelCompetencesLangagieres, labelComprehensionEcrite }) => {
  const diffPercentage = hasNatData ? percentage - pourcentageReference : null;

  const getEvolutionColor = (diff) => {
    if (diff > 0) return 'text-emerald-600';
    if (diff < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const getEvolutionIcon = (diff) => {
    if (diff > 0) return <TrendingUp className="w-4 h-4" />;
    if (diff < 0) return <TrendingDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getColorForPercentage = (pct) => {
    if (pct < 40) return 'bg-gradient-to-r from-red-400 via-red-500 to-red-600';
    if (pct < 70) return 'bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600';
    return 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600';
  };

  const getLabel = () => {
    if (testSpecifique === "Automatismes" && labelAutomatisme) return labelAutomatisme[label];
    if (testSpecifique === "Problèmes" && labelProblèmes) return labelProblèmes[label];
    if (testSpecifique === "Lexique" && labelLexique) return labelLexique[label];
    if (testSpecifique === "CompetencesLangagieres" && labelCompetencesLangagieres) return labelCompetencesLangagieres[label];
    if (testSpecifique === "ComprehensionEcrite" && labelComprehensionEcrite) return labelComprehensionEcrite[label];
    return '';
  };

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 transition-all duration-200 border-b border-gray-100 last:border-b-0
        ${isActive
          ? 'bg-gradient-to-r from-blue-100 to-cyan-100 border-l-4 !border-l-blue-600 shadow-sm'
          : 'hover:bg-gray-50'
        }
      `}
    >
      <div className="flex items-start justify-between mb-2">
        <span className={`text-xs font-bold px-2 py-1 rounded-md ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
          {label}
        </span>
        {hasNatData ? (
          <div className={`flex items-center gap-1 text-sm font-semibold ${getEvolutionColor(diffPercentage)}`}>
            {getEvolutionIcon(diffPercentage)}
            {diffPercentage > 0 ? '+' : ''}{diffPercentage}%
          </div>
        ) : (
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-md">Pas de réf.</span>
        )}
      </div>

      <p className={`text-sm text-gray-700 line-clamp-2 ${extrait ? 'mb-1' : 'mb-3'}`}>{getLabel()}</p>
      {extrait ? <p className="text-xs italic text-gray-500 line-clamp-1 mb-3">«&nbsp;{extrait}&nbsp;»</p> : null}

      {/* Mini progress bars */}
      <div className="space-y-1.5">
        {hasNatData ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-9">Nat.</span>
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getColorForPercentage(pourcentageReference)} rounded-full transition-all duration-500`}
                style={{ width: `${pourcentageReference}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 w-9 text-right">{pourcentageReference}%</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-9">Nat.</span>
            <span className="flex-1 text-xs text-gray-400 italic">Aucune donnée</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-9">Bilan</span>
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getColorForPercentage(percentage)} rounded-full transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 w-9 text-right">{percentage}%</span>
        </div>
      </div>
    </button>
  );
};




  